import { useEffect, useState } from 'react';
import { GEO_ENDPOINT, IPAPI_URL, GEO_TIMEOUT_MS } from '../config';

export type GeoResult = {
  cc: string | null;
  lat: number | null;
  lon: number | null;
  city?: string | null;
};

const CACHE_KEY = 'bn_geo';

/**
 * Fetches visitor geolocation for drawing the highlighted arc on the globe.
 *
 * Order of attempts:
 *   1. Same-origin `/api/geo` — Cloudflare Pages Function reading request.cf.
 *      Free, unlimited, ~0ms edge latency. Works in production (Cloudflare).
 *   2. ipapi.co — only used if #1 returns null coords (typically dev server
 *      where Pages Functions are inactive, or non-CF hosting).
 *
 * Result is cached in sessionStorage so subsequent renders don't re-fetch.
 * Failures are silent — the visitor arc simply doesn't draw.
 */
export function useGeoDetection(): GeoResult {
  const [geo, setGeo] = useState<GeoResult>({ cc: null, lat: null, lon: null });

  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        setGeo(JSON.parse(cached));
        return;
      } catch {
        /* fall through */
      }
    }

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), GEO_TIMEOUT_MS);

    const finish = (result: GeoResult) => {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
      setGeo(result);
    };

    const tryIpApi = () =>
      fetch(IPAPI_URL, { signal: ctrl.signal })
        .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
        .then(data =>
          finish({
            cc: data.country_code || null,
            lat: data.latitude ?? null,
            lon: data.longitude ?? null,
            city: data.city || null
          })
        )
        .catch(err => console.warn('geo (ipapi) failed:', err));

    fetch(GEO_ENDPOINT, { signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(data => {
        // If CF gave us real coords, we're done. Otherwise fall through.
        if (data && data.lat != null && data.lon != null) {
          finish({ cc: data.cc, lat: data.lat, lon: data.lon, city: data.city });
        } else {
          return tryIpApi();
        }
      })
      .catch(() => tryIpApi())
      .finally(() => clearTimeout(timer));

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, []);

  return geo;
}
